import {
  ActionIcon,
  Box,
  Group,
  Menu,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconChevronDown, IconTrash } from '@tabler/icons-react';
import { Fragment } from 'react';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type FamilyMember = ReturnType<typeof useFamilyMembers>['members'][number];

export type OwnerRowValue = {
  id?: string | null;
  client_id?: string | null;
  external_name?: string | null;
  ownership_percentage?: number | null;
};

const EXTERNAL = 'external';

// Products whose backend persists an owners list. The owner editor must only run for
// these; other products (crypto, collectibles, pensions) have no owner tables, so
// collecting owners for them would silently drop them on save.
const OWNER_SUPPORTED_PRODUCTS = new Set([
  'Property',
  'InvestmentAndCash',
  'Mortgage',
  'Loan',
  'SpendingAccount',
  'Protection',
]);

export const productSupportsOwners = (product: string): boolean =>
  OWNER_SUPPORTED_PRODUCTS.has(product);

export function ownersSumTo100(owners: OwnerRowValue[]): boolean {
  const sum = owners.reduce((acc, owner) => acc + (Number(owner.ownership_percentage) || 0), 0);
  return Math.abs(sum - 100) < 0.01;
}

type Props = {
  owners: OwnerRowValue[];
  onChange: (next: OwnerRowValue[]) => void;
  members: FamilyMember[];
  /** The item's total value, used for the per-owner money readout. */
  total?: number;
  /**
   * Renders an owner's slice of `total`. Supplied by the caller so each surface keeps its own
   * money convention: the create form uses the tenant's currency, the Data Upload Review card
   * uses the currency the detection itself carries, matching the value shown above it.
   */
  formatAmount: (amount: number) => string;
  /** Create form shows a joint toggle that reveals the rows; review always shows them. */
  showJointToggle?: boolean;
  isJoint?: boolean;
  onJointChange?: (joint: boolean) => void;
  /** Locks the rows once the surface has saved them and further edits would go nowhere. */
  disabled?: boolean;
};

// Controlled owners editor shared by the create form (via OwnerSharesSection) and the
// Data Upload Review card. Owns no form/route state — it renders the given owners and
// reports edits through onChange.
export function OwnersEditor({
  owners,
  onChange,
  members,
  total = 0,
  formatAmount,
  showJointToggle = false,
  isJoint = false,
  onJointChange,
  disabled = false,
}: Props) {
  const usedClientIds = new Set(owners.map(owner => owner.client_id).filter(Boolean));
  const availableMembers = members.filter(member => !usedClientIds.has(member.clientId));

  const memberName = (clientId: string) => {
    const member = members.find(candidate => candidate.clientId === clientId);
    return member ? `${member.client.firstName} ${member.client.lastName}` : 'Unknown owner';
  };

  const addOwner = (value: string) =>
    onChange(
      value === EXTERNAL
        ? [...owners, { external_name: '', ownership_percentage: null }]
        : [...owners, { client_id: value, ownership_percentage: null }],
    );

  const removeOwner = (index: number) => onChange(owners.filter((_, i) => i !== index));

  const updateOwner = (index: number, patch: Partial<OwnerRowValue>) =>
    onChange(owners.map((owner, i) => (i === index ? { ...owner, ...patch } : owner)));

  const shareSum = owners.reduce(
    (acc, owner) => acc + (Number(owner.ownership_percentage) || 0),
    0,
  );
  const sumMatches = Math.abs(shareSum - 100) < 0.01;

  const showRows = showJointToggle ? isJoint : true;

  return (
    <Stack gap="sm">
      <Group justify={showJointToggle ? 'space-between' : 'flex-end'} align="center">
        {showJointToggle && (
          <Switch
            checked={isJoint}
            disabled={disabled}
            onChange={event => onJointChange?.(event.currentTarget.checked)}
            label="Joint asset. Enter each owner's share."
          />
        )}
        {showRows && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="tertiary"
                size="xs"
                px={9}
                aria-label="Add owner"
                disabled={disabled}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  Add owner
                  <IconChevronDown size={16} color="var(--mantine-color-brand-7)" />
                </span>
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {availableMembers.length > 0 && (
                <Fragment>
                  <Menu.Label>Family members</Menu.Label>
                  {availableMembers.map(member => (
                    <Menu.Item key={member.clientId} onClick={() => addOwner(member.clientId)}>
                      {`${member.client.firstName} ${member.client.lastName}`}
                      {member.isMain ? ' (Main)' : ''}
                    </Menu.Item>
                  ))}
                </Fragment>
              )}
              <Menu.Item onClick={() => addOwner(EXTERNAL)}>Add external owner</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {showRows && (
        <Stack gap="xs">
          {owners.map((owner, index) => {
            const percentage = Number(owner.ownership_percentage) || 0;
            return (
              <Group key={owner.id ?? index} align="center" wrap="nowrap" gap="md">
                <Box style={{ width: 200 }}>
                  {owner.client_id ? (
                    <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {memberName(owner.client_id)}
                    </Text>
                  ) : (
                    <TextInput
                      placeholder="Owner name"
                      disabled={disabled}
                      value={owner.external_name ?? ''}
                      onChange={event =>
                        updateOwner(index, { external_name: event.currentTarget.value })
                      }
                    />
                  )}
                </Box>
                <Box
                  style={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    background: 'var(--mantine-color-gray-1)',
                  }}
                >
                  <Box
                    style={{
                      height: 8,
                      borderRadius: 4,
                      width: `${Math.min(100, percentage)}%`,
                      background: 'var(--mantine-color-brand-3)',
                    }}
                  />
                </Box>
                <NumberInput
                  w={90}
                  min={0}
                  max={100}
                  disabled={disabled}
                  suffix="%"
                  value={owner.ownership_percentage ?? ''}
                  onChange={value =>
                    updateOwner(index, {
                      ownership_percentage: value === '' ? null : Number(value),
                    })
                  }
                />
                <Text size="sm" w={110} ta="right" c="gray.10">
                  {formatAmount((total * percentage) / 100)}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  disabled={disabled}
                  onClick={() => removeOwner(index)}
                  aria-label="Remove owner"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            );
          })}

          {owners.length > 0 && !sumMatches && (
            <Text size="xs" c="error.6">
              Shares must add up to 100% (currently {shareSum}%).
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}
